<template>
  <div class="fixed top-0 w-full z-100">
    <!-- Mobile header -->
    <header id="header-mobile" class="block lg:hidden">
      <nav>
        <div class="bg-foreground fixed flex h-48 items-center shadow-lg w-full z-100">
          <button class="pr-24 pl-16 accessible-border-mobile" @click="openMobileNav">
            <span class="sr-only">Open main navigation</span>
            <svg v-if="!mobileNavOpen" width="18" height="20" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Open main navigation</title>
              <path
                d="M1 2C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0H17C17.5523 0 18 0.447715 18 1C18 1.55228 17.5523 2 17 2H1Z"
                fill="#FF0064"
              />
              <path
                d="M1 8C0.447715 8 0 7.55228 0 7C0 6.44772 0.447715 6 1 6H17C17.5523 6 18 6.44772 18 7C18 7.55228 17.5523 8 17 8H1Z"
                fill="#FF0064"
              />
              <path
                d="M0 13C0 13.5523 0.447715 14 1 14H17C17.5523 14 18 13.5523 18 13C18 12.4477 17.5523 12 17 12H1C0.447715 12 0 12.4477 0 13Z"
                fill="#FF0064"
              />
            </svg>
            <svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Close main navigation</title>
              <path
                d="M0.292893 15.7071C0.683418 16.0976 1.31658 16.0976 1.70711 15.7071L8 9.41421L14.2929 15.7071C14.6834 16.0976 15.3166 16.0976 15.7071 15.7071C16.0976 15.3166 16.0976 14.6834 15.7071 14.2929L9.41421 8L15.7071 1.70711C16.0976 1.31658 16.0976 0.683417 15.7071 0.292893C15.3166 -0.0976311 14.6834 -0.0976311 14.2929 0.292893L8 6.58579L1.70711 0.292893C1.31658 -0.0976311 0.683418 -0.0976311 0.292893 0.292893C-0.0976309 0.683417 -0.0976309 1.31658 0.292893 1.70711L6.58579 8L0.292893 14.2929C-0.0976311 14.6834 -0.0976311 15.3166 0.292893 15.7071Z"
                fill="#FF0064"
              />
            </svg>
          </button>

          <a href="/" class="accessible-border-mobile">
            <img src="@/images/components/header/sittercity-mark-logo.svg" alt="Sittercity" class="w-36" />
          </a>

          <a class="flex items-center ml-auto pr-16 accessible-border-mobile" href="/messages">
            <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Messages</title>
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M2 0H18C19.1046 0 20 0.89543 20 2V14C20 15.1046 19.1046 16 18 16H2C0.89543 16 0 15.1046 0 14V2C0 0.89543 0.89543 0 2 0ZM2 3.37073V14H18V3.37073L10 10.8707L2 3.37073ZM3.46211 2L10 8.12927L16.5379 2H3.46211Z"
                fill="#4B4C52"
              />
            </svg>
            <span
              v-if="unreadMessageCount > 0"
              class="nav-unread-messages-count bg-brand-contrast text-center text-foreground font-semibold inline-block h-20 w-20 rounded-full text-xs tracking-tighter leading-5 ml-8"
              >{{ unreadMessageCount }}</span
            >
          </a>
        </div>

        <div v-if="mobileNavOpen" class="bg-foreground fixed pt-48 h-screen w-full z-101">
          <div class="border-b-1 border-solid border-grey-background-contrast py-24 px-8">
            <form
              class="border-1 border-solid border-grey-divider-subdued flex m-auto navigation-search shadow-lg accessible-border"
              method="get"
              action="/masthead_search"
              role="search"
            >
              <button class="px-16" type="submit">
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <title>Search</title>
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6.83008 12C3.51637 12 0.830078 9.31371 0.830078 6C0.830078 2.68629 3.51637 0 6.83008 0C10.1438 0 12.8301 2.68629 12.8301 6C12.8301 7.34075 12.3903 8.57879 11.6472 9.57774L14.643 12.5451C14.9863 12.8852 14.9889 13.4392 14.6489 13.7825C14.3088 14.1259 13.7548 14.1285 13.4114 13.7884L10.41 10.8155C9.41065 11.5596 8.1718 12 6.83008 12ZM6.83008 10.25C9.17729 10.25 11.0801 8.34721 11.0801 6C11.0801 3.65279 9.17729 1.75 6.83008 1.75C4.48287 1.75 2.58008 3.65279 2.58008 6C2.58008 8.34721 4.48287 10.25 6.83008 10.25Z"
                    fill="#282930"
                  />
                </svg>
              </button>
              <label for="search_strategy_mobile" class="sr-only">Find care</label>
              <select
                id="search_strategy_mobile"
                class="text-m px-8 py-12 w-3/5 select border-r-1 border-solid border-grey-background-contrast"
                name="search_strategy"
                :value="selectedCareType"
              >
                <option value="babysitter_job">Babysitting jobs</option>
                <option value="nanny_job">Nanny jobs</option>
                <option value="special_needs_job">Special needs jobs</option>
                <option value="companion_care_job">Care companion jobs</option>
                <option value="pet_care_job">Pet care jobs</option>
                <option value="housekeeping_job">Housekeeping jobs</option>
              </select>
              <label for="masthead_zipcode_mobile" class="sr-only">Enter zip code here</label>
              <input
                id="masthead_zipcode_mobile"
                class="pl-16 pr-8 w-1/3"
                type="text"
                name="masthead_zipcode"
                placeholder="in zip"
                maxlength="5"
                :value="defaultZipCode"
              />
            </form>
          </div>
          <ul class="pt-40 pl-64">
            <li class="pb-20">
              <a href="/account/providers/dashboard" class="text-grey-text-subdued text-m1 link--tertiary">Home</a>
            </li>
            <li class="pb-20">
              <a href="/adventures" class="text-grey-text-subdued text-m1 link--tertiary">Adventures</a>
            </li>
            <li class="pb-20">
              <a href="/availability" class="text-grey-text-subdued text-m1 link--tertiary">Availability</a>
            </li>
            <li class="pb-20">
              <a href="/provider/bookings" class="text-grey-text-subdued text-m1 link--tertiary">Bookings</a>
            </li>
            <li class="pb-20">
              <a href="https://www.sittercity.com/trust-and-safety" class="text-grey-text-subdued text-m1 link--tertiary"
                >Trust &amp; Safety</a
              >
            </li>
            <li class="pb-20">
              <a href="https://support.sittercity.com/" rel="external" class="text-grey-text-subdued text-m1 link--tertiary"
                >Help</a
              >
            </li>
            <li class="pb-20">
              <a href="/account/settings" class="text-grey-text-subdued text-m1 link--tertiary">Account</a>
            </li>
            <li class="pb-20">
              <a href="/logout" class="text-grey-text-subdued text-m1 link--tertiary">Log out</a>
            </li>
          </ul>
        </div>
      </nav>
    </header>

    <!-- Desktop header -->
    <header
      id="header-desktop"
      class="hidden lg:flex bg-foreground border-b-1 border-solid border-grey-divider-subdued w-full z-100"
      :class="{ 'shadow-lg': hasScrolled }"
    >
      <nav class="flex grow items-center">
        <a href="/" class="px-32">
          <img src="@/images/components/header/sittercity-mark-logo.svg" alt="Sittercity" class="w-48" />
        </a>

        <div>
          <form
            class="border-1 border-solid border-grey-divider-subdued flex m-auto navigation-search shadow-lg"
            method="get"
            action="/masthead_search"
            role="search"
          >
            <button class="px-16 mr-2 accessible-border" type="submit">
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <title>Search</title>
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6.83008 12C3.51637 12 0.830078 9.31371 0.830078 6C0.830078 2.68629 3.51637 0 6.83008 0C10.1438 0 12.8301 2.68629 12.8301 6C12.8301 7.34075 12.3903 8.57879 11.6472 9.57774L14.643 12.5451C14.9863 12.8852 14.9889 13.4392 14.6489 13.7825C14.3088 14.1259 13.7548 14.1285 13.4114 13.7884L10.41 10.8155C9.41065 11.5596 8.1718 12 6.83008 12ZM6.83008 10.25C9.17729 10.25 11.0801 8.34721 11.0801 6C11.0801 3.65279 9.17729 1.75 6.83008 1.75C4.48287 1.75 2.58008 3.65279 2.58008 6C2.58008 8.34721 4.48287 10.25 6.83008 10.25Z"
                  fill="#282930"
                />
              </svg>
            </button>
            <label for="search_strategy_desktop" class="sr-only">Find care</label>
            <select
              id="search_strategy_desktop"
              class="text-m px-8 py-12 w-3/5 mr-2 select border-r-1 border-solid border-grey-background-contrast accessible-border"
              name="search_strategy"
              :value="selectedCareType"
            >
              <option value="babysitter_job">Babysitting jobs</option>
              <option value="nanny_job">Nanny jobs</option>
              <option value="special_needs_job">Special needs jobs</option>
              <option value="companion_care_job">Care companion jobs</option>
              <option value="pet_care_job">Pet care jobs</option>
              <option value="housekeeping_job">Housekeeping jobs</option>
            </select>
            <label for="masthead_zipcode_desktop" class="sr-only">Enter zip code here</label>
            <input
              id="masthead_zipcode_desktop"
              class="pl-16 pr-8 w-1/3 accessible-border"
              type="text"
              name="masthead_zipcode"
              placeholder="in zip"
              maxlength="5"
              :value="defaultZipCode"
            />
          </form>
        </div>

        <ul class="flex ml-auto pt-28 text-grey-clickable pr-24">
          <li class="mr-24">
            <a
              href="/adventures"
              class="border-b-2 border-solid border-transparent hover:border-brand-contrast link--tertiary accessible-border pt-18 pb-18 pl-10 pr-10"
              >Adventures</a
            >
          </li>
          <li class="mr-24">
            <a
              href="/availability"
              class="border-b-2 border-solid border-transparent hover:border-brand-contrast link--tertiary accessible-border pt-18 pb-18 pl-10 pr-10"
              >Availability</a
            >
          </li>
          <li class="mr-24">
            <a
              href="/provider/bookings"
              class="border-b-2 border-solid border-transparent -mr-16 hover:border-brand-contrast link--tertiary accessible-border pt-18 pb-18 pl-10 pr-10"
              >Bookings</a
            >
          </li>
          <li class="flex mr-24">
            <a
              class="border-b-2 border-solid border-transparent -mr-16 pb-32 pt-4 hover:border-brand-contrast accessible-border"
              href="/messages"
            >
              <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <title>Messages</title>
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M2 0H18C19.1046 0 20 0.89543 20 2V14C20 15.1046 19.1046 16 18 16H2C0.89543 16 0 15.1046 0 14V2C0 0.89543 0.89543 0 2 0ZM2 3.37073V14H18V3.37073L10 10.8707L2 3.37073ZM3.46211 2L10 8.12927L16.5379 2H3.46211Z"
                  fill="#4B4C52"
                />
              </svg>
            </a>
            <span
              v-if="unreadMessageCount > 0"
              class="nav-unread-messages-count bg-brand-contrast text-center text-foreground font-semibold inline-block h-20 w-20 rounded-full text-xs tracking-tighter leading-5 ml-8 relative top-[-6px]"
              >{{ unreadMessageCount }}</span
            >
          </li>
          <li ref="desktopDropdownContainer">
            <button class="flex items-center accessible-border" @click="toggleDesktopDropdown">
              {{ firstName }}
              <svg
                aria-hidden="true"
                class="ml-4"
                :class="{ 'rotate-180 transform': desktopDropdownOpen }"
                width="10"
                height="6"
                viewBox="0 0 10 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.664906 0.5H9.33509C9.62634 0.5 9.82797 0.635602 9.93999 0.906805C10.052 1.17801 10.0072 1.41124 9.80557 1.60651L5.47048 5.80473C5.33605 5.93491 5.17923 6 5 6C4.82077 6 4.66395 5.93491 4.52952 5.80473L0.194431 1.60651C-0.00720122 1.41124 -0.0520082 1.17801 0.0600095 0.906805C0.172027 0.635602 0.37366 0.5 0.664906 0.5Z"
                  fill="#4B4C52"
                />
              </svg>
            </button>

            <div v-if="desktopDropdownOpen" class="absolute bg-foreground p-12 right-6 shadow-lg top-14 z-101">
              <button class="flex items-center ml-auto mr-6 mb-16" @click="toggleDesktopDropdown">
                {{ firstName }}
                <svg
                  class="ml-4 rotate-180 transform"
                  aria-hidden="true"
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.664906 0.5H9.33509C9.62634 0.5 9.82797 0.635602 9.93999 0.906805C10.052 1.17801 10.0072 1.41124 9.80557 1.60651L5.47048 5.80473C5.33605 5.93491 5.17923 6 5 6C4.82077 6 4.66395 5.93491 4.52952 5.80473L0.194431 1.60651C-0.00720122 1.41124 -0.0520082 1.17801 0.0600095 0.906805C0.172027 0.635602 0.37366 0.5 0.664906 0.5Z"
                    fill="#4B4C52"
                  />
                </svg>
              </button>

              <ul>
                <li class="pb-12">
                  <a href="/account/settings">Account</a>
                </li>
                <li class="pb-12">
                  <a href="https://www.sittercity.com/trust-and-safety">Trust &amp; Safety</a>
                </li>
                <li class="pb-12">
                  <a href="https://support.sittercity.com/" rel="external">Help</a>
                </li>
                <li class="pb-12">
                  <a href="/logout" rel="nofollow">Log out</a>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    </header>
  </div>
</template>

<script lang="ts">
import debounce from 'lodash/debounce';

export default {
  name: 'SitterNav',
  data() {
    return {
      mobileNavOpen: false,
      desktopDropdownOpen: false,
      hasScrolled: false,
      handleScroll: null as any,
      handleClickOutside: null as any,
    };
  },
  computed: {
    account() {
      return this.$store.getters['account/getAccount'];
    },
    firstName(): string {
      return this.account?.firstName || '';
    },
    selectedCareType(): string {
      const careTypeMap: Record<string, string> = {
        babysitter: 'babysitter_job',
        nanny: 'nanny_job',
        special_needs: 'special_needs_job',
        companion_care: 'companion_care_job',
        pet_care: 'pet_care_job',
        housekeeping: 'housekeeping_job',
      };

      const params = new URLSearchParams(window.location.search);
      const careType = params.get('careType');
      if (careType && careTypeMap[careType]) {
        return careTypeMap[careType];
      }

      const accountCareType = this.account?.careType?.toLowerCase();
      if (accountCareType && careTypeMap[accountCareType]) {
        return careTypeMap[accountCareType];
      }

      return '';
    },
    defaultZipCode(): string {
      const params = new URLSearchParams(window.location.search);
      return params.get('zipcode') || this.$store.getters['account/getZipCode'] || '';
    },
    unreadMessageCount(): number {
      return this.$store.getters['messages/unreadCount'];
    },
  },
  created() {
    this.handleScroll = debounce(() => {
      this.hasScrolled = window.scrollY > 0;
    }, 250);
    this.handleClickOutside = (event: Event) => {
      const dropdownContainer = this.$refs.desktopDropdownContainer as HTMLElement;
      if (dropdownContainer && !dropdownContainer.contains(event.target as Node)) {
        this.desktopDropdownOpen = false;
      }
    };
    window.addEventListener('scroll', this.handleScroll);
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    this.$store.dispatch('messages/startPolling');
  },
  beforeDestroy() {
    this.$store.dispatch('messages/stopPolling');
    document.body.classList.remove('overflow-hidden');
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    openMobileNav() {
      this.mobileNavOpen = !this.mobileNavOpen;
      document.body.classList.toggle('overflow-hidden', this.mobileNavOpen);
    },
    toggleDesktopDropdown() {
      this.desktopDropdownOpen = !this.desktopDropdownOpen;
    },
  },
};
</script>
